import Lenis from 'lenis';

/**
 * Lenis smooth scrolling controller
 * Manages smooth scrolling behavior and integrates with existing scroll handlers
 */
export default class LenisController {
  constructor(options = {}) {
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Don't initialize Lenis if user prefers reduced motion
    if (this.prefersReducedMotion) {
      console.log('[Lenis] Skipping initialization due to reduced motion preference');
      return;
    }

    this.options = {
      duration: 1.6,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: true,
      smoothTouch: false, // Keep native touch scrolling on mobile
      infinite: false,
      wheelMultiplier: 0.7, // Heavier scroll feel
      touchMultiplier: 1.5, // Touch scroll sensitivity
      normalizeWheel: true, // Normalize wheel delta across browsers
      // Prevent Lenis from handling scroll on specific elements
      // so native wheel can scroll internal containers (e.g. footer)
      prevent: (node) => {
        try {
          if (!node || typeof node.closest !== 'function') return false;
          return Boolean(
            node.closest('.footer-scroll') ||
            node.closest('[data-lenis-prevent]')
          );
        } catch { return false; }
      },
      ...options
    };

    this.init();
  }

  init() {
    try {
      this.lenis = new Lenis(this.options);

      // Expose globally for other modules
      window.__lenis = this.lenis;

      // Start RAF loop
      this.startRafLoop();

      // Setup scroll event delegation
      this.setupScrollEvents();
      // Ensure internal scroll containers don't bubble to Lenis
      this.setupPreventZones();

      console.log('[Lenis] Initialized successfully');
    } catch (error) {
      console.error('[Lenis] Failed to initialize:', error);
    }
  }

  startRafLoop() {
    const raf = (time) => {
      this.lenis.raf(time);
      requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);
  }

  setupPreventZones() {
    try {
      const zones = document.querySelectorAll('.footer-scroll, [data-lenis-prevent]');
      zones.forEach((el) => {
        // Helper to check if element can scroll further in wheel direction
        const canScrollY = (element, deltaY) => {
          try {
            const top = element.scrollTop;
            const max = element.scrollHeight - element.clientHeight;
            if (deltaY > 0) return top < (max - 1);
            if (deltaY < 0) return top > 0;
            return false;
          } catch { return false; }
        };

        // Wheel should scroll the element itself; bubble only when at the ends
        el.addEventListener('wheel', (e) => {
          const dy = e.deltaY || 0;
          if (canScrollY(el, dy)) {
            // Let the element handle it natively; avoid Lenis/window
            e.stopPropagation();
          } else {
            // At the edge: allow bubbling so parent (or guide) can handle step/handoff
          }
        }, { passive: false });
        // Touch move inside the zone should not bubble
        el.addEventListener('touchmove', (e) => {
          e.stopPropagation();
        }, { passive: true });

        // Do not pause Lenis; rely on prevent+stopPropagation for stability
      });
    } catch { }
  }

  setupScrollEvents() {
    // Forward lenis scroll events to window for compatibility
    this.lenis.on('scroll', (e) => {
      // Dispatch custom event for other modules to listen to
      window.dispatchEvent(new CustomEvent('lenis-scroll', {
        detail: {
          scroll: e.scroll,
          velocity: e.velocity,
          direction: e.direction,
          progress: e.progress
        }
      }));
    });
  }

  // Public API methods
  scrollTo(target, options = {}) {
    if (!this.lenis) return;
    this.lenis.scrollTo(target, options);
  }

  stop() {
    if (!this.lenis) return;
    this.lenis.stop();
  }

  start() {
    if (!this.lenis) return;
    this.lenis.start();
  }

  destroy() {
    if (!this.lenis) return;
    this.lenis.destroy();
    window.__lenis = null;
  }

  // Temporarily disable smooth scrolling (for programmatic scrolls)
  disable() {
    if (!this.lenis) return;
    this.lenis.stop();
  }

  enable() {
    if (!this.lenis) return;
    this.lenis.start();
  }

  // Global helper functions for other modules
  static disable() {
    if (window.__lenis) {
      window.__lenis.stop();
    }
  }

  static enable() {
    if (window.__lenis) {
      window.__lenis.start();
    }
  }

  static isEnabled() {
    return window.__lenis && window.__lenis.isScrolling !== false;
  }
}
